import deepMerge                from 'deepmerge';
export { default as deepMerge } from 'deepmerge';
export * from './PropertyPath';
export function deepMergeAssign(
	target   : object,
	sources  : object[],
	options? : deepMerge.Options
) {
	const merged = deepMerge.all(
		[ target, ...sources ],
		Object.assign({
			isMergeableObject : (x : any) => (
				x &&
				x.constructor &&
				((x instanceof Array) || (x.constructor === Object))
			)
		}, options)
	);
	return (Object.assign(target, merged));
};
export interface TClass<T, A extends any[]> extends Function {
	new(...args: A): T;
};
export type PropertyType<TObj, TProp extends keyof TObj> = TObj[TProp];
export const flatten = (() => {
	const flatten = (
		objIn  : {},
		objOut : Record<any, any>,
		path   : string
	) => {
		Object.entries(objIn).forEach(function(x) {
			const k = (path === '') ? x[0] : path + x[0];
			if (x[1] instanceof Object) {
				flatten(x[1], objOut, k + '.');
			} else {
				objOut[k] = x[1];
			}
		});
		return (objOut);
	};
	return ((obj : {}) => {
		return (flatten(obj, {}, ''));
	});
})();
export function getSync(
	root : Record<any, any>,
	path : string[]
) {
	let obj : Record<any, any> | unknown = root;
	let p : any = null;
	for (const key of path) {
		if ((obj === null) || (typeof obj !== 'object')) {
			return (undefined);
		}
		p   = obj;
		obj = (<any>obj)[ key ];
	}
	if (typeof obj === 'function') {
		const _c = obj;
		obj = function(this : unknown, ...x : any[]) {
			const self = this ? this : p;
			const res  = _c.call(self, ...x);
			return (res);
		};
	}
	return (obj);
};
export function get(
	root : Record<any, any>,
	path : string[]
) {
	let obj : Record<any, any> | unknown = root;
	let p : any = null;
	for (const key of path) {
		if ((obj === null) || (typeof obj !== 'object')) {
			return (undefined);
		}
		p   = obj;
		obj = (<any>obj)[ key ];
	}
	if (typeof obj === 'function') {
		const _c = obj;
		obj = async function(this : unknown, ...x : any[]) {
			const self = this ? this : p;
			const res  = await _c.call(self, ...x);
			return (res);
		};
		// obj = obj.bind(p);
	}
	return (obj);
};
export function getPathFromAtoB(
	a : object,
	b : object
) {
	const f2 = (
		a           : object,
		b           : object,
		currentPath : string[]
	) : string[] | null => {
		const toInspectDeepen = [];
		let found : string[] | null = null;
		for (const entries of Object.entries(a)) {
			if (entries[1] === b) {
				found = [ ...currentPath, entries[0] ];
			} else if (typeof entries[1] === 'object') {
				toInspectDeepen.push([ [ ...currentPath, entries[0]], entries[1] ]);
			}
		}
		if (found) {
			return (found);
		}
		for (const toInspect of toInspectDeepen) {
			const result = f2(toInspect[1], b, toInspect[0]);
			if (result) {
				return (result);
			}
		}
		return (null);
	};
	return (f2(a, b, []));
}
export function getInheritanceChainFromClass(
	_class : TClass<unknown, any>
) {
	const classes = [ ];
	while (_class) {
		classes.push(_class);
		if (_class === Object) {
			break;
		}
		_class = _class.prototype.__proto__.constructor;
	}
	return (classes);
};
export function getInheritanceChainFromInstance(
	instance : { constructor : TClass<unknown, any> }
) {
	return (getInheritanceChainFromClass(instance.constructor));
};
export function setCompareVersion<T>(
	_new : Set<T>,
	_old : Set<T>
) {
	const diff = {
		added   : <T[]>[],
		removed : <T[]>[],
		kept    : <T[]>[]
	};
	for (const n of _new) {
		if (_old.has(n)) {
			diff.kept.push(n);
		} else {
			diff.added.push(n);
		}
	}
	for (const o of _old) {
		if (!_new.has(o)) {
			diff.removed.push(o);
		}
	}
	return (diff);
};
export function setTakeOne<T>(
	set : Set<T>
) {
	if (set.size === 0) {
		return (undefined);
	}
	const item = <T>set.values().next().value;
	set.delete(item);
	return (item);
};
export function fromKeyValueList(
	from : [ key : string, value : any ][]
) : Record<string, any> {
	return (from.reduce((a, c) => {
		a[c[ 0 ]] = c[1];
		return (a);
	}, <Record<string, any>>{}));
};
const internalHasSameContent = (isDeepCheck : boolean, LHS : object, RHS : object) : boolean => {
	const entriesLHS = Object.entries(LHS);
	const entriesRHS = Object.entries(RHS);
	const self       = internalHasSameContent.bind(null, isDeepCheck);
	type IU          = Iterable<unknown>;
	const selfAF     = (x : IU, y : IU) => self(Array.from(x), Array.from(y));
	return (
		(entriesLHS.length !== entriesRHS.length)      ? false :
		((LHS instanceof Set) || (LHS instanceof Map)) ? self([ LHS ], [ RHS ]) :
		(
			entriesLHS.map(x => [ x[1], (entriesRHS.find(y => y[0] === x[0]) || [])[1] ]).
			filter(xy => {
				const [ x, y, tx ] = [ ...xy, typeof xy[0] ];
				return (
					((!x && y) || (x && !y))          ? true                              :
					(!x && !y)                        ? false                             :
					(tx !== typeof y)                 ? true                              :
					(tx !== 'object')                 ? (x !== y)                         :
					(x.constructor !== y.constructor) ? true                              :
					!isDeepCheck                      ? (x !== y)                         : (
						(x instanceof Set)              ? !selfAF(x.values(), y.values())   :
						(x instanceof Map)              ? !selfAF(x.entries(), y.entries()) :
						!self(x, y)
					)
				);
			})
			.length
		) === 0
	);
};
export const hasSameContent = internalHasSameContent.bind(null, false);
export const hasSameContentDeeply = internalHasSameContent.bind(null, true);

export function copyWithout<T extends object>(
	object : T,
	withoutProperties : string[]
) : object {
	return (fromKeyValueList(
		Object.entries(object).
		filter(([k]) => !withoutProperties.includes(k))
	));
};
// export function copyWithout<T extends object>(
// 	object : T,
// 	withoutProperties : string[]
// ) : Omit<T, typeof withoutProperties[0]> {
// 	return (fromKeyValueList(
// 		Object.entries(object).
// 		filter(([k]) => !withoutProperties.includes(k))
// 	));
// };

export type JSONScalar = (
	string  |
	boolean |
	number  |
	null
);
export type JSONScalarArray = JSONScalar[];
export type JSONScalarRecord = Record<string | number, JSONScalar>;
export type JSONComplex = (
	JSONScalarArray   |
	JSONScalarArray[] |
	JSONScalarRecord  |
	JSONScalarRecord[]
);
export type JSONComplexRecursive = (
	JSONComplex[] |
	Record<
		string | number,
		JSONComplex | JSONComplex[] |
		Record<
			string | number,
			JSONComplex | JSONComplex[]
		>
	>
);
export type JSON = (
	JSONScalar       |
	JSONScalarArray  |
	JSONScalarRecord |
	JSONComplex      |
	JSONComplexRecursive
);
